export interface MissionCase {
  id?: string;
  organization_id?: string;
  created_by?: string;
  name: string;
  city?: string | null;
  state?: string | null;
  urgency?: string;
  summary?: string | null;
  needs?: string[];
  patron_saint?: string | null;
  situation_type?: string | null;
  contact?: string | null;
  description?: string | null;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface MissionParticipation {
  id?: string;
  case_id: string;
  user_id?: string;
  organization_id?: string;
  participation_type: string;
  commitments?: string[];
  status?: string;
  created_at?: string;
}

export interface MissionReport {
  id?: string;
  participation_id: string;
  user_id?: string;
  organization_id?: string;
  report: string;
  needs_continuity?: boolean;
  new_needs_identified?: string | null;
  created_at?: string;
}

export interface MissionStats {
  missions_completed: number;
  people_helped: number;
  active_volunteers: number;
}

interface ApiResponse<T = any> {
  message: string;
  data: T;
  status: number;
}

class MissionService {
  private baseUrl: string;

  constructor(baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3030') {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = document.cookie.split('sgo_access_token=')[1]?.split(';')[0];
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Cases
  async getCases(): Promise<MissionCase[]> {
    const response = await this.request<MissionCase[]>('/missions/cases', { method: 'GET' });
    return response.data;
  }

  async getCase(id: string): Promise<MissionCase> {
    const response = await this.request<MissionCase>(`/missions/cases/${id}`, { method: 'GET' });
    return response.data;
  }

  async createCase(caseData: Partial<MissionCase>): Promise<MissionCase> {
    const response = await this.request<MissionCase>('/missions/cases', {
      method: 'POST',
      body: JSON.stringify(caseData),
    });
    return response.data;
  }

  async updateCase(id: string, updates: Partial<MissionCase>): Promise<MissionCase> {
    const response = await this.request<MissionCase>(`/missions/cases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return response.data;
  }

  async deleteCase(id: string): Promise<void> {
    await this.request<null>(`/missions/cases/${id}`, { method: 'DELETE' });
  }

  // Participations
  async getMyParticipations(): Promise<any[]> {
    const response = await this.request<any[]>('/missions/participations/me', { method: 'GET' });
    return response.data;
  }

  async createParticipation(data: Partial<MissionParticipation>): Promise<MissionParticipation> {
    const response = await this.request<MissionParticipation>('/missions/participations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  // Reports
  async getMyReports(): Promise<any[]> {
    const response = await this.request<any[]>('/missions/reports/me', { method: 'GET' });
    return response.data;
  }

  async createReport(data: Partial<MissionReport>): Promise<MissionReport> {
    const response = await this.request<MissionReport>('/missions/reports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  // Stats
  async getStats(): Promise<MissionStats> {
    const response = await this.request<MissionStats>('/missions/stats', { method: 'GET' });
    return response.data;
  }
}

const missionService = new MissionService();
export default missionService;
